import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowRight, 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Calendar, 
  Clock,
  FileCheck,
  Shield, 
  Info, 
  HelpCircle,
  TrendingUp,
  XCircle,
  Zap,
  Gift
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CFELogo } from "@/components/CFELogo";

export default function CFEPremiereAnnee() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "CFE auto-entrepreneur première année : exonération, règles et obligations";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Êtes-vous exonéré de CFE la première année ? Découvrez les règles d'exonération, le seuil de 5000€ et vos obligations déclaratives en tant qu'auto-entrepreneur.");
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = "Êtes-vous exonéré de CFE la première année ? Découvrez les règles d'exonération, le seuil de 5000€ et vos obligations déclaratives en tant qu'auto-entrepreneur.";
      document.head.appendChild(meta);
    }

    return () => {
      document.title = "CFE Auto-Entrepreneur | Déclaration CFE simplifiée - Formulaire 1447-C-SD";
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <a href="/" className="flex items-center gap-2">
              <CFELogo size={28} />
              <span className="font-bold text-lg text-[#0F172A]">CFE Auto-Entrepreneur</span>
            </a>
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"
            >
              <a href="/#formulaire" className="flex items-center text-white no-underline">
                Commencer
              </a>
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="prose prose-slate max-w-none">
          
          {/* Breadcrumb */}
          <nav className="text-sm text-[#64748B] mb-8" aria-label="Breadcrumb">
            <a href="/" className="hover:text-[#2563EB]">Accueil</a>
            <span className="mx-2">/</span>
            <span className="text-[#0F172A]">CFE auto-entrepreneur première année</span>
          </nav>

          {/* H1 - Main title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6 leading-tight">
            CFE auto-entrepreneur première année : êtes-vous exonéré ?
          </h1>

          {/* Introduction */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <p className="text-lg text-[#475569] leading-relaxed mb-4">
              La <strong>Cotisation Foncière des Entreprises (CFE)</strong> est souvent source d&apos;inquiétude pour les nouveaux auto-entrepreneurs. 
              Faut-il la payer dès la première année ? Quel est le montant ? Existe-t-il des exonérations ?
            </p>
            <p className="text-[#475569] leading-relaxed mb-4">
              La CFE est un impôt local qui concerne toutes les entreprises, y compris les <strong>auto-entrepreneurs</strong> et <strong>micro-entrepreneurs</strong>. 
              Elle est calculée sur la valeur locative des biens utilisés pour votre activité professionnelle.
            </p>
            <p className="text-[#475569] leading-relaxed m-0">
              <strong>Bonne nouvelle</strong> : en tant que nouvel auto-entrepreneur, vous bénéficiez d&apos;une <strong>exonération automatique de CFE</strong> 
              lors de votre première année civile d&apos;activité. Ce guide vous explique tout ce que vous devez savoir.
            </p>
          </div>

          {/* Disclaimer Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-8 flex items-start gap-3">
            <Info className="h-5 w-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[#475569]">
              <p className="font-medium text-[#0F172A] mb-1">Information importante</p>
              <p className="m-0">
                Ce guide est fourni à titre informatif. Ce service n&apos;est pas affilié à l&apos;administration fiscale française. 
                Pour toute situation particulière, consultez votre Service des Impôts des Entreprises (SIE).
              </p>
            </div>
          </div>

          {/* Table of contents */}
          <Card className="mb-10 bg-slate-50 border-slate-200">
            <CardContent className="p-6">
              <p className="font-semibold text-[#0F172A] mb-4">Sommaire</p>
              <ul className="space-y-2 text-[#475569] list-none pl-0 m-0">
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">1.</span>
                  <a href="#exoneration" className="hover:text-[#2563EB] no-underline">Exonération de CFE la première année</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">2.</span>
                  <a href="#exceptions" className="hover:text-[#2563EB] no-underline">Cas où la CFE reste due malgré la première année</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">3.</span>
                  <a href="#seuil" className="hover:text-[#2563EB] no-underline">Seuil de chiffre d&apos;affaires (5 000 €)</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">4.</span>
                  <a href="#formulaire-obligatoire" className="hover:text-[#2563EB] no-underline">Pourquoi remplir le formulaire 1447-C-SD même en cas d&apos;exonération</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">5.</span>
                  <a href="#bien-declarer" className="hover:text-[#2563EB] no-underline">Comment déclarer correctement pour bénéficier de l&apos;exonération</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">6.</span>
                  <a href="#generer" className="hover:text-[#2563EB] no-underline">Générer votre déclaration CFE en toute simplicité</a>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 1: Exonération de CFE la première année */}
          <section id="exoneration" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <Gift className="h-6 w-6 text-[#2563EB]" />
              Exonération de CFE la première année
            </h2>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Bonne nouvelle pour les nouveaux auto-entrepreneurs</h3>
                  <p className="text-[#475569] m-0">
                    Tous les auto-entrepreneurs bénéficient d&apos;une <strong>exonération totale de CFE</strong> lors de leur première année civile d&apos;activité. 
                    Cette exonération est <strong>automatique</strong> et s&apos;applique quelle que soit la date de création dans l&apos;année.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Principe de l&apos;exonération automatique</h3>
            <p className="text-[#475569] mb-4">
              L&apos;exonération de CFE la première année est prévue par l&apos;article 1478 du Code général des impôts. 
              Elle concerne toutes les entreprises nouvellement créées, y compris les auto-entrepreneurs et micro-entrepreneurs.
            </p>
            <p className="text-[#475569] mb-6">
              Cette exonération est <strong>de droit</strong>, c&apos;est-à-dire qu&apos;elle s&apos;applique automatiquement sans démarche particulière. 
              Cependant, vous devez tout de même remplir le formulaire 1447-C-SD pour vous faire connaître de l&apos;administration fiscale.
            </p>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Année civile de création</h3>
            <p className="text-[#475569] mb-4">
              L&apos;exonération s&apos;applique pour l&apos;<strong>année civile complète</strong> de création de votre auto-entreprise, 
              c&apos;est-à-dire du 1er janvier au 31 décembre.
            </p>

            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
              <p className="font-medium text-[#0F172A] mb-2">Exemples concrets</p>
              <ul className="space-y-2 text-[#475569] text-sm m-0">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Création le <strong>15 janvier 2025</strong> → Exonération pour toute l&apos;année 2025</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Création le <strong>1er décembre 2025</strong> → Exonération pour toute l&apos;année 2025</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Création le <strong>31 décembre 2025</strong> → Exonération pour toute l&apos;année 2025</span>
                </li>
              </ul>
            </div>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Différence entre création et début d&apos;activité</h3>
            <p className="text-[#475569] mb-4">
              La date prise en compte pour l&apos;exonération est la <strong>date officielle de création</strong> de votre auto-entreprise, 
              c&apos;est-à-dire la date d&apos;immatriculation figurant sur votre certificat d&apos;inscription au répertoire SIRENE.
            </p>
            <p className="text-[#475569] mb-4">
              Cette date peut être différente de la date à laquelle vous avez réellement commencé à exercer votre activité ou à facturer vos premiers clients.
            </p>

            {/* Attention box */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1">Attention</p>
              <p className="text-[#475569] text-sm m-0">
                Si vous avez demandé une date de début d&apos;activité antérieure à la date d&apos;immatriculation, 
                c&apos;est cette date antérieure qui sera prise en compte pour déterminer votre première année d&apos;exonération.
              </p>
            </div>
          </section>

          {/* Section 2: Cas où la CFE reste due malgré la première année */}
          <section id="exceptions" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-[#2563EB]" />
              Cas où la CFE reste due malgré la première année
            </h2>

            <p className="text-[#475569] mb-6">
              Bien que l&apos;exonération de première année soit la règle générale, il existe certaines situations où vous pourriez 
              quand même être redevable de la CFE :
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Reprise d&apos;activité après cessation</h3>
                    <p className="text-[#475569] text-sm m-0">
                      Si vous avez déjà exercé la même activité par le passé et que vous la reprenez, 
                      l&apos;exonération de première année peut ne pas s&apos;appliquer selon les circonstances.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Transfert d&apos;établissement</h3>
                    <p className="text-[#475569] text-sm m-0">
                      Un simple déménagement de votre activité d&apos;une commune à une autre ne vous donne pas droit 
                      à une nouvelle exonération de première année.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Erreurs dans la déclaration initiale</h3>
                    <p className="text-[#475569] text-sm m-0">
                      Si vous n&apos;avez pas rempli correctement le formulaire 1447-C-SD ou si vous avez oublié de le transmettre, 
                      l&apos;administration peut vous imposer d&apos;office et l&apos;exonération peut être remise en cause.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Activités spécifiques exclues</h3>
                    <p className="text-[#475569] text-sm m-0">
                      Certaines activités (notamment certaines professions libérales réglementées) 
                      peuvent avoir des règles d&apos;exonération différentes. Vérifiez auprès de votre SIE.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border-l-4 border-[#2563EB] p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1 flex items-center gap-2">
                <Info className="h-4 w-4 text-[#2563EB]" />
                À retenir
              </p>
              <p className="text-[#475569] text-sm m-0">
                Dans la grande majorité des cas, si vous créez votre première auto-entreprise, 
                vous bénéficierez bien de l&apos;exonération de CFE pour votre première année civile d&apos;activité.
              </p>
            </div>
          </section>

          {/* Section 3: Seuil de chiffre d'affaires (5 000 €) */}
          <section id="seuil" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <TrendingUp className="h-6 w-6 text-[#2563EB]" />
              Seuil de chiffre d&apos;affaires (5 000 €)
            </h2>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Exonération permanente sous 5 000 € de CA</h3>
                  <p className="text-[#475569] m-0">
                    Au-delà de la première année, si votre <strong>chiffre d&apos;affaires annuel</strong> reste inférieur à <strong>5 000 €</strong>, 
                    vous êtes <strong>exonéré de CFE de façon permanente</strong>, pas seulement la première année.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Explication simple</h3>
            <p className="text-[#475569] mb-4">
              Depuis 2019, les auto-entrepreneurs dont le chiffre d&apos;affaires annuel est inférieur ou égal à 5 000 € 
              sont <strong>totalement exonérés de CFE</strong>, et ce de manière permanente (pas seulement la première année).
            </p>
            <p className="text-[#475569] mb-6">
              Ce seuil s&apos;apprécie sur l&apos;année civile précédente. Par exemple, pour la CFE 2025, 
              c&apos;est votre chiffre d&apos;affaires de 2024 qui est pris en compte.
            </p>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Impact sur la CFE</h3>
            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 text-[#0F172A] font-semibold">Chiffre d&apos;affaires (N-1)</th>
                    <th className="text-left py-2 text-[#0F172A] font-semibold">CFE due ?</th>
                  </tr>
                </thead>
                <tbody className="text-[#475569]">
                  <tr className="border-b border-slate-100">
                    <td className="py-2">0 € à 5 000 €</td>
                    <td className="py-2 text-green-600 font-medium">Non (exonéré)</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2">5 001 € à 10 000 €</td>
                    <td className="py-2 text-amber-600 font-medium">Oui (cotisation minimum)</td>
                  </tr>
                  <tr>
                    <td className="py-2">Plus de 10 000 €</td>
                    <td className="py-2 text-amber-600 font-medium">Oui (selon barème communal)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Cas des micro-entrepreneurs</h3>
            <p className="text-[#475569] mb-4">
              En tant que micro-entrepreneur (auto-entrepreneur), vous êtes soumis aux mêmes règles que les autres entreprises. 
              Le seuil de 5 000 € s&apos;applique de la même manière.
            </p>

            {/* Bon à savoir */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1">Bon à savoir</p>
              <p className="text-[#475569] text-sm m-0">
                Même si vous êtes exonéré grâce au seuil de 5 000 €, vous recevrez peut-être un avis de CFE à 0 €. 
                C&apos;est normal : l&apos;administration vous envoie l&apos;avis pour confirmer votre exonération.
              </p>
            </div>
          </section>

          {/* Section 4: Pourquoi remplir le formulaire 1447-C-SD même en cas d'exonération */}
          <section id="formulaire-obligatoire" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <FileText className="h-6 w-6 text-[#2563EB]" />
              Pourquoi remplir le formulaire 1447-C-SD même en cas d&apos;exonération
            </h2>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-[#2563EB] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Obligation déclarative même si vous êtes exonéré</h3>
                  <p className="text-[#475569] m-0">
                    L&apos;exonération de première année ne vous dispense pas de remplir et d&apos;envoyer le formulaire 1447-C-SD. 
                    Cette déclaration est <strong>obligatoire</strong> pour tous les nouveaux entrepreneurs.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Obligation déclarative</h3>
            <p className="text-[#475569] mb-4">
              Le formulaire 1447-C-SD (Déclaration initiale de CFE) doit être rempli et envoyé au Service des Impôts des Entreprises 
              <strong> avant le 31 décembre de l&apos;année de création</strong> de votre auto-entreprise.
            </p>
            <p className="text-[#475569] mb-6">
              Cette déclaration permet à l&apos;administration fiscale de vous enregistrer et de préparer le calcul de votre CFE 
              pour les années suivantes.
            </p>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Erreurs courantes</h3>
            <ul className="space-y-2 text-[#475569] mb-6">
              <li className="flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span>Penser que l&apos;exonération dispense de toute démarche</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span>Oublier d&apos;envoyer le formulaire avant le 31 décembre</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span>Ne pas cocher la case &quot;entreprise nouvelle&quot;</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span>Indiquer une mauvaise date de création</span>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Risques en cas d&apos;oubli</h3>
            <p className="text-[#475569] mb-4">
              Si vous ne remplissez pas le formulaire 1447-C-SD, vous vous exposez à plusieurs risques :
            </p>
            <ul className="space-y-2 text-[#475569] mb-6">
              <li className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span><strong>Taxation d&apos;office</strong> : l&apos;administration peut vous imposer sur la base d&apos;estimations</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span><strong>Perte de l&apos;exonération</strong> : votre statut d&apos;&quot;entreprise nouvelle&quot; peut ne pas être reconnu</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span><strong>Pénalités de retard</strong> : des majorations peuvent s&apos;appliquer</span>
              </li>
            </ul>

            {/* Conseil box */}
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Notre conseil
              </p>
              <p className="text-[#475569] text-sm m-0">
                Ne prenez pas de risque : remplissez et envoyez le formulaire 1447-C-SD dès la création de votre auto-entreprise, 
                même si vous savez que vous serez exonéré la première année.
              </p>
            </div>
          </section>

          {/* Section 5: Comment déclarer correctement pour bénéficier de l'exonération */}
          <section id="bien-declarer" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <FileCheck className="h-6 w-6 text-[#2563EB]" />
              Comment déclarer correctement pour bénéficier de l&apos;exonération
            </h2>

            <p className="text-[#475569] mb-6">
              Pour vous assurer de bien bénéficier de l&apos;exonération de CFE la première année, 
              voici les points clés à respecter lors du remplissage du formulaire 1447-C-SD :
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">1</span>
                  <h3 className="text-lg font-semibold text-[#0F172A] m-0">Case &quot;entreprise nouvelle&quot;</h3>
                </div>
                <p className="text-[#475569] text-sm m-0">
                  Dans la section A3 du formulaire (Origine de l&apos;établissement), cochez bien la case <strong>&quot;Création&quot;</strong>. 
                  C&apos;est cette information qui permet à l&apos;administration de vous identifier comme une entreprise nouvelle 
                  et de vous appliquer l&apos;exonération automatique.
                </p>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">2</span>
                  <h3 className="text-lg font-semibold text-[#0F172A] m-0">Importance de la date</h3>
                </div>
                <p className="text-[#475569] text-sm m-0">
                  Indiquez précisément la <strong>date de création officielle</strong> de votre auto-entreprise 
                  (celle figurant sur votre certificat d&apos;inscription au répertoire SIRENE). 
                  Cette date détermine votre année d&apos;exonération.
                </p>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">3</span>
                  <h3 className="text-lg font-semibold text-[#0F172A] m-0">Sections à ne pas négliger</h3>
                </div>
                <ul className="space-y-1 text-[#475569] text-sm m-0">
                  <li>• <strong>Section A1</strong> : Vos informations d&apos;identification (SIRET, adresse, activité)</li>
                  <li>• <strong>Section A3</strong> : Origine de l&apos;établissement (case &quot;Création&quot;)</li>
                  <li>• <strong>Section D</strong> : Exonérations (cochez celles auxquelles vous avez droit)</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">4</span>
                  <h3 className="text-lg font-semibold text-[#0F172A] m-0">Délai à respecter</h3>
                </div>
                <p className="text-[#475569] text-sm m-0">
                  Le formulaire doit être envoyé <strong>avant le 31 décembre</strong> de l&apos;année de création de votre auto-entreprise. 
                  N&apos;attendez pas la dernière minute pour éviter tout problème.
                </p>
              </div>
            </div>

            {/* Tip box */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1">Conseil pratique</p>
              <p className="text-[#475569] text-sm m-0">
                Conservez une copie de votre formulaire 1447-C-SD et la preuve d&apos;envoi (accusé de réception si envoi postal, 
                ou confirmation si dépôt en ligne). Cela pourra vous être utile en cas de litige.
              </p>
            </div>
          </section>

          {/* Section 6: Générer votre déclaration CFE en toute simplicité */}
          <section id="generer" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <Zap className="h-6 w-6 text-[#2563EB]" />
              Générer votre déclaration CFE en toute simplicité
            </h2>

            <p className="text-[#475569] mb-6">
              Le formulaire 1447-C-SD peut sembler complexe avec ses nombreuses rubriques. 
              Pour vous simplifier la tâche, utilisez notre <strong>assistant guidé</strong> qui vous accompagne étape par étape.
            </p>

            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Pourquoi utiliser notre assistant ?</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="bg-[#2563EB] rounded-full p-1.5 flex-shrink-0">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A] mb-1">Conformité garantie</p>
                    <p className="text-[#475569] text-sm m-0">Structure identique au formulaire officiel 1447-C-SD</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-[#2563EB] rounded-full p-1.5 flex-shrink-0">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A] mb-1">Rapidité</p>
                    <p className="text-[#475569] text-sm m-0">Complétez votre déclaration en moins de 5 minutes</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-[#2563EB] rounded-full p-1.5 flex-shrink-0">
                    <HelpCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A] mb-1">Clarté</p>
                    <p className="text-[#475569] text-sm m-0">Chaque question est expliquée simplement</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-[#2563EB] rounded-full p-1.5 flex-shrink-0">
                    <FileCheck className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A] mb-1">PDF prêt à envoyer</p>
                    <p className="text-[#475569] text-sm m-0">Document professionnel à télécharger immédiatement</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
              <h3 className="font-semibold text-[#0F172A] mb-3">Comment ça fonctionne ?</h3>
              <ol className="space-y-2 text-[#475569] list-decimal pl-5 m-0">
                <li>Répondez aux questions de l&apos;assistant (votre identité, votre activité, etc.)</li>
                <li>Vérifiez l&apos;aperçu de votre déclaration</li>
                <li>Téléchargez votre formulaire 1447-C-SD au format PDF</li>
                <li>Envoyez le document à votre Service des Impôts des Entreprises</li>
              </ol>
            </div>

            {/* Reassurance */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#64748B] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#475569]">
                <p className="font-medium text-[#0F172A] mb-1">Mentions importantes</p>
                <ul className="space-y-1 m-0 pl-0 list-none">
                  <li>• Ce service est indépendant de l&apos;administration fiscale française</li>
                  <li>• Le document généré doit être vérifié par vos soins avant envoi</li>
                  <li>• Ce service ne constitue pas un conseil fiscal personnalisé</li>
                </ul>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <a href="/#formulaire">
                <Button 
                  size="lg"
                  className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg"
                >
                  Commencer ma déclaration CFE
                  <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
                </Button>
              </a>
            </div>
          </section>

          {/* FAQ Section for Rich Snippets */}
          <section id="faq" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <HelpCircle className="h-6 w-6 text-[#2563EB]" />
              Questions fréquentes sur la CFE première année
            </h2>

            <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
              
              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Suis-je automatiquement exonéré de CFE la première année ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Oui, tous les auto-entrepreneurs bénéficient d&apos;une exonération totale de CFE 
                    lors de leur première année civile d&apos;activité. Cette exonération est automatique 
                    mais vous devez quand même remplir le formulaire 1447-C-SD.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Dois-je payer la CFE si mon chiffre d&apos;affaires est inférieur à 5 000 € ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Non, si votre chiffre d&apos;affaires annuel est inférieur ou égal à 5 000 €, 
                    vous êtes exonéré de CFE de façon permanente, pas seulement la première année. 
                    Cette règle s&apos;applique depuis 2019.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Que se passe-t-il si je ne remplis pas le formulaire 1447-C-SD ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    L&apos;absence de déclaration peut entraîner une taxation d&apos;office par l&apos;administration fiscale, 
                    la perte de votre exonération de première année, et des pénalités de retard. 
                    Il est donc essentiel de remplir ce formulaire même si vous êtes exonéré.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Quand dois-je envoyer le formulaire 1447-C-SD ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Le formulaire 1447-C-SD doit être envoyé au Service des Impôts des Entreprises 
                    avant le 31 décembre de l&apos;année de création de votre auto-entreprise. 
                    Par exemple, si vous créez votre entreprise en mars 2025, vous devez envoyer le formulaire avant le 31 décembre 2025.
                  </p>
                </div>
              </div>

            </div>
          </section>

          {/* CTA Final */}
          <section className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-[#0F172A] mb-3">
              Prêt à remplir votre déclaration CFE ?
            </h2>
            <p className="text-[#475569] mb-6">
              Même si vous êtes exonéré la première année, le formulaire 1447-C-SD reste obligatoire. 
              Générez-le en quelques minutes grâce à notre assistant guidé.
            </p>
            <a href="/#formulaire">
              <Button 
                size="lg"
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg w-full sm:w-auto"
              >
                Commencer ma déclaration CFE
                <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
              </Button>
            </a>
          </section>

        </article>
      </main>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-12 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CFELogo size={28} />
                <span className="font-bold text-lg">CFE Auto-Entrepreneur</span>
              </div>
              <p className="text-slate-400 text-sm">
                Service de génération de déclaration CFE simplifié pour les auto-entrepreneurs français.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Liens utiles</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/#formulaire" className="text-slate-400 hover:text-[#2563EB]">Remplir ma déclaration</a></li>
                <li><a href="/#faq" className="text-slate-400 hover:text-[#2563EB]">FAQ</a></li>
                <li><a href="/comment-remplir-cfe-auto-entrepreneur" className="text-slate-400 hover:text-[#2563EB]">Guide CFE auto-entrepreneur</a></li>
                <li><a href="/mentions-legales" className="text-slate-400 hover:text-[#2563EB]">Mentions légales</a></li>
                <li><a href="/cgv" className="text-slate-400 hover:text-[#2563EB]">CGV</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Informations importantes</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Ce service n&apos;est pas affilié à l&apos;administration fiscale française.</li>
                <li>Ce service ne constitue pas un conseil fiscal.</li>
                <li>Le document généré doit être vérifié par l&apos;utilisateur avant envoi.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-slate-400 text-sm mb-2">Une question ? Contactez-nous :</p>
              <a 
                href="mailto:contact@cfe-autoentrepreneur.fr" 
                className="text-sm text-slate-400 hover:text-[#2563EB] transition-colors"
              >
                contact@cfe-autoentrepreneur.fr
              </a>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>© {new Date().getFullYear()} CFE Auto-Entrepreneur. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
